import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePhaSearch, type Pha } from "@/hooks/use-pha";
import { cn } from "@/lib/utils";

/**
 * Searchable PHA picker backed by the HUD directory. Pre-filters by the
 * property's `state`, distance-sorts by its coordinates, and falls back to a
 * free-typed custom name when the authority isn't in the directory.
 */
export function PhaCombobox({ value, state, lat, lng, onSelect, onManual, onClear }: {
  value: string;
  state?: string;
  lat?: number;
  lng?: number;
  onSelect: (pha: Pha) => void;
  onManual: (name: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQuery(input), 250);
    return () => clearTimeout(t);
  }, [input]);

  const { phas, isLoading } = usePhaSearch({ query, state, lat, lng, enabled: open });
  const typed = input.trim();

  return (
    // `modal` lets the list scroll while inside the wizard Dialog — without it
    // the Dialog's scroll-lock blocks wheel events on the portaled popover.
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setInput(""); }}>
      <PopoverTrigger asChild>
        <Button id="phaName" variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal">
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || "Search PHA by name…"}
          </span>
          {value ? (
            <span role="button" tabIndex={0} aria-label="Clear PHA selection"
              className="ml-2 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClear(); } }}>
              <X className="h-4 w-4" />
            </span>
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={input} onValueChange={setInput} placeholder="Type a PHA name…" />
          <CommandList>
            {isLoading && (
              <div className="py-6 text-center text-sm text-muted-foreground">Searching…</div>
            )}
            {!isLoading && phas.length === 0 && !typed && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {state ? `No PHAs found in ${state}.` : "Type to search PHAs."}
              </div>
            )}
            {!isLoading && phas.length > 0 && (
              <CommandGroup heading={typed ? "Matching PHAs (all states)" : state ? `PHAs in ${state}` : "PHAs"}>
                {phas.map((pha) => (
                  <CommandItem key={pha.participant_code} value={pha.participant_code}
                    onSelect={() => { onSelect(pha); setOpen(false); }}>
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === pha.pha_name ? "opacity-100" : "opacity-0")} />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground">{pha.pha_name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[pha.city, pha.state].filter(Boolean).join(", ") || "—"}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {typed && (
              <CommandGroup>
                <CommandItem value={`__manual__${typed}`} onSelect={() => { onManual(typed); setOpen(false); }}>
                  Use “{typed}” as a custom PHA name
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
